import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { publicAPI } from '../../services/api';
import InstructorSignupForm from '../publicaccess/InstructorSignupForm';

const InstructorSignupPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [token, setToken] = useState(null);
  const [validating, setValidating] = useState(true);
  const [validationError, setValidationError] = useState(null);
  const [invitationData, setInvitationData] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');

  useEffect(() => {
    const invitationToken = searchParams.get('token');
    
    if (!invitationToken) {
      setValidationError('No invitation token provided');
      setValidating(false);
      return;
    }

    setToken(invitationToken);
    validateToken(invitationToken);
  }, [searchParams]);

  const validateToken = async (token) => {
    try {
      console.log('=== VALIDATING INSTRUCTOR TOKEN ===');

      
      const response = await publicAPI.validateInstructorToken(token);
      
      console.log('=== VALIDATION SUCCESS ===');

      
      if (response.success) {
        setInvitationData(response.data);
        setValidating(false);
      } else {
        setValidationError('Invalid invitation token');
        setValidating(false);
      }
    } catch (error) {
      console.error('=== VALIDATION ERROR ===');
      console.error('Error:', error);
      console.error('Error message:', error.message);
      console.error('=======================');
      
      setValidationError(error.message || 'Failed to validate invitation token');
      setValidating(false);
    }
  };

  const handleSignupSubmit = async (formData) => {
    setSubmitting(true);
    setSubmitError(null);
    
    try {
      console.log('=== SUBMITTING INSTRUCTOR SIGNUP ===');
      
      // Add token to form data
      const signupData = {
        token,
        ...formData
      };
      
      console.log('=== SENDING TO API ===');

      
      const response = await publicAPI.instructorSignup(signupData);
      
      console.log('=== INSTRUCTOR SIGNUP SUCCESS ===');

      
      if (response.success) {
        setRegisteredEmail(formData.email || '');
        setRegistrationSuccess(true);
      } else {
        // If response has an error but didn't throw
        throw new Error(response.error || 'Instructor signup failed');
      }
    } catch (error) {
      console.error('=== INSTRUCTOR SIGNUP ERROR ===');
      console.error('Error:', error);
      console.error('Error message:', error.message);
      console.error('==============================');
      
      // Set user-friendly error message
      let errorMessage = error.message || 'Failed to complete registration';
      
      // Handle specific errors
      if (errorMessage.includes('Invalid invitation token')) {
        errorMessage = 'This invitation link is invalid. Please request a new invitation.';
      } else if (errorMessage.includes('expired')) {
        errorMessage = 'This invitation has expired. Please request a new invitation.';
      } else if (errorMessage.includes('already been used')) {
        errorMessage = 'This invitation has already been used. If you need to create a new account, please request a new invitation.';
      } else if (errorMessage.includes('Email does not match')) {
        errorMessage = 'The email address does not match the invitation. Please use the email address that received the invitation.';
      } else if (errorMessage.includes('already exists')) {
        // Backend returns 409 with "An instructor with this email already exists"
        errorMessage = 'An instructor with this email already exists. Please sign in instead, or use a different email address.';
      }
      
      setSubmitError(errorMessage);
      
      // Navigate to support page with error info
      navigate('/support', {
        state: {
          error: {
            message: errorMessage,
            type: 'signup',
            context: 'instructor_registration'
          }
        }
      });
    } finally {
      setSubmitting(false);
    }
  };


  if (registrationSuccess) {
    return (
      <div style={{
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        minHeight: '100vh', backgroundColor: '#f9fafb', padding: '2rem 1rem'
      }}>
        <div style={{
          maxWidth: '30rem', width: '100%', padding: '2.5rem 2rem',
          backgroundColor: 'white', borderRadius: '1rem',
          boxShadow: '0 4px 24px rgba(0,0,0,0.08)', textAlign: 'center'
        }}>
          {/* Tick icon */}
          <div style={{
            width: '4rem', height: '4rem', margin: '0 auto 1.25rem',
            background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
            borderRadius: '50%', display: 'flex',
            alignItems: 'center', justifyContent: 'center'
          }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none"
              stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>

          <h2 style={{
            fontSize: '1.5rem', fontWeight: 700, color: '#111827',
            margin: '0 0 0.5rem'
          }}>
            Registration Successful!
          </h2>

          <p style={{ fontSize: '1rem', color: '#6b7280', margin: '0 0 1.5rem', lineHeight: 1.6 }}>
            Your instructor account has been created. You can now sign in using your email and password.
          </p>

          {registeredEmail && (
            <div style={{
              background: '#f5f3ff', borderRadius: '0.5rem',
              padding: '0.75rem 1rem', marginBottom: '1.75rem',
              fontSize: '0.9rem', color: '#4f46e5', fontWeight: 600,
              wordBreak: 'break-all'
            }}>
              {registeredEmail}
            </div>
          )}

          <button
            onClick={() => window.location.href = '/'}
            style={{
              width: '100%', padding: '0.75rem',
              background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
              color: 'white', border: 'none', borderRadius: '0.6rem',
              fontSize: '1rem', fontWeight: 600, cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(99,102,241,0.35)'
            }}
          >
            Go to Sign In
          </button>
        </div>
      </div>
    );
  }

  if (validating) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: '#f9fafb'
      }}>
        <div style={{
          textAlign: 'center',
          padding: '2rem'
        }}>
          <div className="spinner" style={{
            width: '3rem',
            height: '3rem',
            margin: '0 auto 1rem',
            border: '4px solid #e5e7eb',
            borderTopColor: '#2563eb',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <p style={{ fontSize: '1.125rem', color: '#6b7280' }}>
            Validating invitation...
          </p>
        </div>
      </div>
    );
  }

  if (validationError) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: '#f9fafb'
      }}>
        <div style={{
          maxWidth: '28rem',
          width: '100%',
          padding: '2rem',
          backgroundColor: 'white',
          borderRadius: '0.5rem',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <div style={{
            width: '3rem',
            height: '3rem',
            margin: '0 auto 1rem',
            backgroundColor: '#fee2e2',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.5rem',
            color: '#ef4444'
          }}>
            ✕
          </div>
          <h2 style={{
            fontSize: '1.5rem',
            fontWeight: 'bold',
            color: '#111827',
            marginBottom: '0.5rem'
          }}>
            Invalid Invitation
          </h2>
          <p style={{
            fontSize: '1rem',
            color: '#6b7280',
            marginBottom: '1.5rem'
          }}>
            {validationError}
          </p>
          <p style={{
            fontSize: '0.875rem',
            color: '#9ca3af',
            marginBottom: '1.5rem'
          }}>
            The invitation link may have expired or is invalid. Please contact the administrator for a new invitation.
          </p>
          <button
            onClick={() => window.location.href = '/'}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#2563eb',
              color: 'white',
              border: 'none',
              borderRadius: '0.375rem',
              fontSize: '1rem',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f9fafb',
      padding: '2rem 1rem'
    }}>
      <div style={{
        maxWidth: '64rem',
        margin: '0 auto'
      }}>
        <div style={{
          textAlign: 'center',
          marginBottom: '2rem'
        }}>
          <h1 style={{
            fontSize: '2rem',
            fontWeight: 'bold',
            color: '#111827',
            marginBottom: '0.5rem'
          }}>
            Instructor Registration
          </h1>
          <p style={{
            fontSize: '1.125rem',
            color: '#6b7280'
          }}>
            You've been invited to join as a driving instructor. Please fill out the form below to create your account.
          </p>
          {invitationData && (
            <p style={{
              fontSize: '0.875rem',
              color: '#9ca3af',
              marginTop: '0.5rem'
            }}>
              Registering as: <strong>{invitationData.email}</strong>
            </p>
          )}
        </div>

        {/* Error Message */}
        {submitError && (
          <div style={{
            maxWidth: '48rem',
            margin: '0 auto 1.5rem',
            padding: '1rem',
            backgroundColor: '#fee2e2',
            border: '1px solid #ef4444',
            borderRadius: '0.5rem',
            color: '#991b1b'
          }}>
            <strong>Error:</strong> {submitError}
          </div>
        )}

        {/* Instructor Signup Form */}
        <InstructorSignupForm
          onSubmit={handleSignupSubmit}
          prefilledEmail={invitationData?.email}
          isSubmitting={submitting}
        />

        {/* Debug info (only in development) */}
        {process.env.NODE_ENV === 'development' && (
          <details style={{ 
            maxWidth: '48rem', 
            margin: '2rem auto', 
            padding: '1rem',
            backgroundColor: 'white',
            borderRadius: '0.5rem',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <summary style={{ cursor: 'pointer', fontWeight: 'bold', marginBottom: '0.5rem' }}>
              Debug Info (Development Only)
            </summary>
            <pre style={{
              padding: '0.5rem',
              backgroundColor: '#f3f4f6',
              borderRadius: '0.25rem',
              fontSize: '0.75rem',
              overflow: 'auto'
            }}>
              {JSON.stringify({
                url: window.location.href,
                token: token,
                tokenLength: token?.length,
                invitationEmail: invitationData?.email,
                invitationId: invitationData?.invitationId,
                invitationType: 'instructor',
                expiresAt: invitationData?.expiresAt
              }, null, 2)}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
};

export default InstructorSignupPage;